import { Micro } from 'effect';
import { buildAuthorizeOptions } from './authorize.request.utils.js';
import { buildParBodyµ, buildParSlimUrlµ, createAuthorizeUrlµ, dispatchAuthorizeFetchµ, dispatchAuthorizeIframeµ, dispatchParRequestµ, generateAuthValuesµ, generatePkceChallengeµ, storeAuthOptionsµ, validateParResponseµ, } from './authorize.request.micros.js';
// ─── Dispatch routing ─────────────────────────────────────────────────────────
/**
 * Routes the authorize URL to the appropriate transport: a direct fetch for
 * pi.flow (PingOne servers reject iframes via X-Frame-Options: DENY), or an
 * iframe for all other response modes.
 */
function dispatchAuthorizeµ(url, options, wellknown, store, log) {
    if (options.responseMode === 'pi.flow') {
        const { responseMode: _, ...redirectOptions } = options;
        return dispatchAuthorizeFetchµ(store, url, wellknown, redirectOptions).pipe(Micro.tap(() => log.debug('Received success response from authorize fetch endpoint')));
    }
    return dispatchAuthorizeIframeµ(store, url, wellknown, options).pipe(Micro.tap(() => log.debug('Received success response from authorize iframe endpoint')));
}
// ─── PAR flow ─────────────────────────────────────────────────────────────────
/**
 * @function createParAuthorizeUrlµ
 * @description Pushed Authorization Request (RFC 9126): POSTs all authorize
 *   parameters to the PAR endpoint, then returns a slim URL containing only
 *   `client_id` and `request_uri` — keeping sensitive params out of the
 *   browser address bar.
 * @param wellknown - The well-known configuration; provides the PAR endpoint
 *   and the authorization endpoint origin used to build the slim URL.
 * @param config - The OIDC client configuration.
 * @param log - CustomLogger; used to warn on short PAR `expires_in` windows.
 * @param store - The RTK client store exposing `oidcApi.endpoints.par`.
 * @param options - Optional request-level overrides; `prompt` is split out
 *   so it appears on the slim URL while the rest of the params go in the
 *   PAR POST body.
 * @returns A `Micro` that resolves to the slim authorize URL string or
 *   fails with a typed `AuthorizationError`.
 */
export function createParAuthorizeUrlµ(wellknown, config, log, store, options) {
    const parEndpoint = wellknown.pushed_authorization_request_endpoint;
    if (!parEndpoint) {
        return Micro.fail({
            error: 'PAR_NOT_CONFIGURED',
            error_description: 'PAR endpoint not found in server configuration',
            type: 'wellknown_error',
        });
    }
    const { prompt, ...parBodyOptions } = options ?? {};
    return Micro.gen(function* () {
        const [authUrlOptions, storeOptions] = yield* generateAuthValuesµ(config, wellknown, options);
        const challenge = yield* generatePkceChallengeµ(authUrlOptions.verifier);
        const body = yield* buildParBodyµ(config, parBodyOptions, challenge, authUrlOptions.state, prompt);
        const parResult = yield* dispatchParRequestµ(store, parEndpoint, body);
        const { request_uri, expires_in } = yield* validateParResponseµ(parResult);
        if (expires_in < 30) {
            yield* Micro.sync(() => log.warn(`PAR request_uri expires in ${expires_in}s — authorize must complete before expiry`));
        }
        yield* storeAuthOptionsµ(storeOptions);
        return yield* buildParSlimUrlµ(wellknown.authorization_endpoint, config.clientId, request_uri, prompt);
    });
}
// ─── Standard + PAR authorize ─────────────────────────────────────────────────
/**
 * @function authorizeµ
 * @description Builds an authorization URL and dispatches the authorize
 *   request, transparently using PAR (RFC 9126) when the server mandates it
 *   via `require_pushed_authorization_requests` or when the consumer opts in
 *   with `config.par`. Otherwise builds a full authorize URL and dispatches
 *   via direct fetch (pi.flow) or iframe.
 * @param wellknown - The well-known configuration for the OIDC server,
 *   including `authorization_endpoint` and (optionally) the PAR endpoint.
 * @param config - The OIDC client configuration (clientId, redirectUri,
 *   scope, responseType, and the optional `par` opt-in flag).
 * @param log - The CustomLogger used for debug and error trace.
 * @param store - The RTK client store providing the `oidcApi` endpoints.
 * @param options - Optional request-level overrides for the authorize call.
 * @param useParFlow - Boolean resolved by the caller (e.g. `client.store`)
 *   indicating whether to use the PAR flow. The caller owns the derivation
 *   logic (`config.par ?? require_pushed_authorization_requests === true`);
 *   this function simply routes on the resolved value.
 * @returns A `Micro` that resolves to an `AuthorizationSuccess` containing
 *   the `code` and `state`, or fails with a typed `AuthorizationError`.
 */
export function authorizeµ(wellknown, config, log, store, options, useParFlow) {
    const parDispatchOptions = {
        clientId: config.clientId,
        redirectUri: config.redirectUri,
        scope: config.scope || 'openid',
        responseType: config.responseType || 'code',
        ...options,
    };
    const parFlow = createParAuthorizeUrlµ(wellknown, config, log, store, options).pipe(Micro.tap((url) => log.debug('PAR authorize URL created', url)), Micro.tapError((err) => Micro.sync(() => log.error(`PAR authorize failed [${err.type}]: ${err.error}`, err))), Micro.flatMap((url) => dispatchAuthorizeµ(url, parDispatchOptions, wellknown, store, log).pipe(Micro.tapError((err) => Micro.sync(() => log.error('Error dispatching PAR authorize request', err))))));
    const [path, opts] = buildAuthorizeOptions(wellknown, config, options);
    const standardFlow = createAuthorizeUrlµ(path, opts).pipe(Micro.tap(([url]) => log.debug('Authorize URL created', url)), Micro.tapError((err) => Micro.sync(() => log.error('Error creating authorize URL', err))), Micro.flatMap(([url, dispatchOpts]) => dispatchAuthorizeµ(url, dispatchOpts, wellknown, store, log)), Micro.tapError((err) => Micro.sync(() => log.error('Error dispatching authorize request', err))));
    return useParFlow ? parFlow : standardFlow;
}
//# sourceMappingURL=authorize.request.js.map